import { reachGoal } from 'application/utils/analytics'
import { CatalogFiltersOptions } from '../../../types'
import { FiltersState } from './_state'
import { FiltersDOM } from './_dom'
import { FiltersURL } from './_url'

/**
 * Класс для управления событиями фильтров
 */
export class FiltersEvents {
  private element: HTMLElement
  private state: FiltersState
  private dom: FiltersDOM
  private url: FiltersURL
  private options: CatalogFiltersOptions
  private initialFiltersSet: boolean = false
  private filterGoalReached = false

  /**
   * Создаёт экземпляр FiltersEvents
   *
   * @param {HTMLElement} element - Корневой элемент
   * @param {FiltersState} state - Состояние фильтров
   * @param {FiltersDOM} dom - DOM менеджер
   * @param {FiltersURL} url - URL менеджер
   * @param {CatalogFiltersOptions} options - Опции фильтров
   */
  constructor(
    element: HTMLElement,
    state: FiltersState,
    dom: FiltersDOM,
    url: FiltersURL,
    options: CatalogFiltersOptions
  ) {
    this.element = element
    this.state = state
    this.dom = dom
    this.url = url
    this.options = options
  }

  public setInitialFiltersSet(value: boolean): void {
    this.initialFiltersSet = value
  }

  /**
   * Применяет фильтры, загруженные из URL при инициализации, — синхронизирует
   * кнопку "Сбросить" и список квестов с текущим состоянием фильтров
   */
  public applyInitialFilters(): void {
    this.updateFilters()
  }

  public bindEvents(): void {
    const resetButton = this.dom.getResetButton()
    const playersButton = this.dom.getPlayersButton()

    // Обработчик кнопки "игры по количеству игроков"
    if (playersButton) {
      playersButton.addEventListener('click', () => {
        this.state.toggleWithPlayers()
        this.dom.updatePlayersButton()
        this.updateFilters()
      })

      const playersCountField = this.dom.getPlayersCountEl()
      if (playersCountField) {
        playersCountField.addEventListener(
          'counterfield:change',
          (event: Event) => {
            const customEvent = event as CustomEvent
            const value = customEvent.detail.value as number

            this.state.setWithPlayers(value || false)
            this.dom.updatePlayersButton()
            this.updateFilters()
          }
        )
      }
    }

    // Переключатель «Только Клаустрофобия»
    const originalButton = this.dom.getOriginalButton()
    if (originalButton) {
      originalButton.addEventListener('click', () => {
        this.state.toggleOriginal()
        this.dom.updateOriginalButton()
        this.updateFilters()
      })
    }

    // Обработчик кнопки "сбросить"
    if (resetButton) {
      resetButton.addEventListener('click', () => {
        this.resetFilters()
      })
    }

    // События от дропдаунов (используя кастомные события)
    this.element.addEventListener('dropdown-change', (event: Event) => {
      const customEvent = event as CustomEvent
      const { name, value } = customEvent.detail

      switch (name) {
        case 'type':
          this.handleTypeChange(value)
          break
        case 'categories':
          this.handleCategoriesChange(value)
          break
        case 'horror':
          this.state.setHorror(Array.isArray(value) ? value : [value])
          this.updateFilters()
          break
        case 'complexity':
          this.state.setComplexity(Array.isArray(value) ? value : [value])
          this.updateFilters()
          break
        case 'metro':
          this.state.setMetro(Array.isArray(value) ? value : [value])
          this.updateFilters()
          break
        case 'age':
          this.state.setAge(value)
          this.updateFilters()
          break
      }
    })
  }

  private handleTypeChange(value: string): void {
    if (!this.initialFiltersSet) {
      this.state.setType(value)
      return
    }

    this.state.setType(value)
    this.state.clearCategories()
    this.dom.updateActiveCategories()
    this.updateFilters()
  }

  private handleCategoriesChange(value: string[]): void {
    if (!this.initialFiltersSet) {
      this.state.setCategories(value)
      return
    }

    this.state.setCategories(value)
    this.updateFilters()
  }

  private updateFilters(): void {
    // Применение фильтров из URL при загрузке — не действие посетителя.
    if (this.initialFiltersSet && !this.filterGoalReached) {
      this.filterGoalReached = true
      reachGoal('new_FilterUse')
    }

    const filters = this.state.getFiltersData()
    this.url.updateURL(filters)
    this.dom.updateResetButton()

    if (this.options.onChange) {
      this.options.onChange(filters)
    }
  }

  private resetFilters(): void {
    this.state.reset()

    this.dom.resetDropdowns()
    this.dom.updatePlayersButton()
    this.dom.updateOriginalButton()
    this.dom.getPlayersCountField()?.setValue(1)
    this.dom.updateActiveCategories()

    // Сбрасываем модальное окно с дополнительными фильтрами
    const event = new CustomEvent('reset-modal-filters')
    this.dom.dispatchEvent(event)

    this.updateFilters()
  }
}
